import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Monitor, Users } from 'lucide-react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatAgo } from '@/shared/lib/format';
import type { DeskQuestion } from '../api/deskApi';

type Props = { questions: DeskQuestion[]; selected: string; onSelect: (id: string) => void };

const DAY_MONTH = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short' });

export const channelName = (q: DeskQuestion) => (q.channel === 'teams' ? 'Teams' : 'Portal');

/** Waiting questions read as how long they have waited; answered ones by date. */
function When({ q }: { q: DeskQuestion }) {
  const waiting = q.status === 'open';
  return (
    <Typography variant="caption" sx={{ color: waiting ? 'warning.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
      {waiting ? formatAgo(q.createdAt) : DAY_MONTH.format(new Date(q.answeredAt ?? q.createdAt))}
    </Typography>
  );
}

/** One row per question: who asked, when, the question and where it came from. */
export function DeskList({ questions, selected, onSelect }: Props) {
  return (
    <Stack component="ul" aria-label="Questions" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {questions.map((q, i) => {
        const isOn = q.id === selected;
        const Icon = q.channel === 'teams' ? Users : Monitor;
        return (
          <li key={q.id}>
            <ButtonBase aria-current={isOn ? 'true' : undefined} onClick={() => { onSelect(q.id); }} sx={{ ...rowSx(isOn), animationDelay: `${i * 40}ms` }}>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, width: '100%' }}>
                <PersonAvatar name={q.askedBy} size={22} />
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0, textAlign: 'left' }}>{q.askedBy}</Typography>
                <When q={q} />
              </Stack>
              <Typography variant="body2" sx={questionSx}>{q.question}</Typography>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <Icon size={13} aria-hidden />
                <Typography variant="caption">{channelName(q)} · {q.id}</Typography>
              </Stack>
            </ButtonBase>
          </li>
        );
      })}
    </Stack>
  );
}

const rowSx = (isOn: boolean) => ({
  width: '100%',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 0.75,
  px: 2,
  py: 1.75,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: isOn ? 'surface.sidebar' : 'transparent',
  boxShadow: isOn ? 'inset 3px 0 0 var(--mui-palette-primary-main)' : 'none',
  transition: 'background-color 150ms, box-shadow 150ms',
  '&:hover': { bgcolor: 'surface.sidebar' },
  '@keyframes rowIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'rowIn 240ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const questionSx = {
  textAlign: 'left',
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;
